import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Colores para los gráficos
const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8dd1e1"];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `chart-tab-${index}`,
    "aria-controls": `chart-tabpanel-${index}`,
  };
}

const GraficasPresupuestosModal = ({ open, handleClose, data, loading = false }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Procesar datos para gráfico por cliente
  const procesarDatosPorCliente = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const clienteData = {};
    
    data.forEach((presupuesto) => {
      const clienteNombre = `${presupuesto.entidad?.descripcion || "Sin nombre"} ${presupuesto.entidad?.apellido || ""}`.trim();
      
      if (!clienteData[clienteNombre]) {
        clienteData[clienteNombre] = {
          nombre: clienteNombre,
          totalPresupuestos: 0,
          cantidadPresupuestos: 0,
        };
      }
      
      clienteData[clienteNombre].totalPresupuestos += presupuesto.montoTotal || 0;
      clienteData[clienteNombre].cantidadPresupuestos += 1;
    });

    return Object.values(clienteData)
      .sort((a, b) => b.totalPresupuestos - a.totalPresupuestos)
      .slice(0, 10); // Top 10 clientes
  };

  // Procesar datos para gráfico por fecha
  const procesarDatosPorFecha = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const fechaData = {};
    
    data.forEach((presupuesto) => {
      const fecha = new Date(presupuesto.fechaHoraCreacion).toLocaleDateString("es-AR");
      
      if (!fechaData[fecha]) {
        fechaData[fecha] = {
          fecha: fecha,
          totalPresupuestos: 0,
          cantidadPresupuestos: 0,
        };
      }
      
      fechaData[fecha].totalPresupuestos += presupuesto.montoTotal || 0;
      fechaData[fecha].cantidadPresupuestos += 1;
    });

    return Object.values(fechaData).sort((a, b) => new Date(a.fecha.split("/").reverse().join("-")) - new Date(b.fecha.split("/").reverse().join("-")));
  };

  // Procesar datos para gráfico por ubicación
  const procesarDatosPorUbicacion = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const ubicacionData = {};
    
    data.forEach((presupuesto) => {
      const ubicacionNombre = presupuesto.ubicacion?.descripcion || "Sin ubicación";
      
      if (!ubicacionData[ubicacionNombre]) {
        ubicacionData[ubicacionNombre] = {
          nombre: ubicacionNombre,
          totalPresupuestos: 0,
          cantidadPresupuestos: 0,
        };
      }
      
      ubicacionData[ubicacionNombre].totalPresupuestos += presupuesto.montoTotal || 0;
      ubicacionData[ubicacionNombre].cantidadPresupuestos += 1;
    });

    return Object.values(ubicacionData);
  };

  // Procesar datos para gráfico por vendedor
  const procesarDatosPorVendedor = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const vendedorData = {};
    
    data.forEach((presupuesto) => {
      const vendedorNombre = presupuesto.usuario?.usuario || "Sin vendedor";
      
      if (!vendedorData[vendedorNombre]) {
        vendedorData[vendedorNombre] = {
          nombre: vendedorNombre,
          totalPresupuestos: 0,
          cantidadPresupuestos: 0,
        };
      }
      
      vendedorData[vendedorNombre].totalPresupuestos += presupuesto.montoTotal || 0;
      vendedorData[vendedorNombre].cantidadPresupuestos += 1;
    });

    return Object.values(vendedorData).sort((a, b) => b.totalPresupuestos - a.totalPresupuestos);
  };

  const datosCliente = procesarDatosPorCliente();
  const datosFecha = procesarDatosPorFecha();
  const datosUbicacion = procesarDatosPorUbicacion();
  const datosVendedor = procesarDatosPorVendedor();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          bgcolor: theme.palette.primary.main,
          color: "white",
        }}
      >
        Análisis de Presupuestos
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, position: "relative", minHeight: 400 }}>
        {loading ? (
          <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={60} color="primary" />
              <Typography variant="h6" color="inherit">
                Cargando datos para análisis de presupuestos...
              </Typography>
              <Typography variant="body2" color="inherit">
                Obteniendo todos los presupuestos filtrados
              </Typography>
            </Box>
          </Backdrop>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="chart tabs"
                centered
              >
                <Tab label="Presupuestos por Cliente" {...a11yProps(0)} />
                <Tab label="Presupuestos por Fecha" {...a11yProps(1)} />
                <Tab label="Presupuestos por Ubicación" {...a11yProps(2)} />
                <Tab label="Presupuestos por Vendedor" {...a11yProps(3)} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Typography variant="h6" gutterBottom>
                Top 10 Clientes por Presupuestos
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={datosCliente}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="nombre" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === "totalPresupuestos" ? `$${value.toLocaleString()}` : value,
                      name === "totalPresupuestos" ? "Total Presupuestos" : "Cantidad Presupuestos"
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="totalPresupuestos" fill="#8884d8" name="Total Presupuestos" />
                  <Bar dataKey="cantidadPresupuestos" fill="#82ca9d" name="Cantidad Presupuestos" />
                </BarChart>
              </ResponsiveContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                Presupuestos por Fecha
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={datosFecha}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === "totalPresupuestos" ? `$${value.toLocaleString()}` : value,
                      name === "totalPresupuestos" ? "Total Presupuestos" : "Cantidad Presupuestos"
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="totalPresupuestos" fill="#8dd1e1" name="Total Presupuestos" />
                  <Bar dataKey="cantidadPresupuestos" fill="#82ca9d" name="Cantidad Presupuestos" />
                </BarChart>
              </ResponsiveContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>
                Distribución por Ubicación
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <ResponsiveContainer width="50%" height={400}>
                  <PieChart>
                    <Pie
                      data={datosUbicacion}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ nombre, totalPresupuestos }) => `${nombre}: $${totalPresupuestos.toLocaleString()}`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="totalPresupuestos"
                    >
                      {datosUbicacion.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, "Total Presupuestos"]} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Typography variant="h6" gutterBottom>
                Presupuestos por Vendedor
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={datosVendedor}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="nombre" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === "totalPresupuestos" ? `$${value.toLocaleString()}` : value,
                      name === "totalPresupuestos" ? "Total Presupuestos" : "Cantidad Presupuestos"
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="totalPresupuestos" fill="#ffc658" name="Total Presupuestos" />
                  <Bar dataKey="cantidadPresupuestos" fill="#ff7c7c" name="Cantidad Presupuestos" />
                </BarChart>
              </ResponsiveContainer>
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GraficasPresupuestosModal;
